import { useQuery } from '@tanstack/react-query';
import { reportedMessagesApi } from '../../api/reportedMessages';

const verdictStyles: Record<string, string> = {
  simulation: 'bg-blue-100 text-blue-800',
  'suspected-phish': 'bg-red-100 text-red-800',
  spam: 'bg-orange-100 text-orange-800',
  unknown: 'bg-gray-100 text-gray-600',
};

export function VerdictBadge({ verdict }: { verdict?: string }) {
  if (!verdict) return null;
  return (
    <span
      className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${verdictStyles[verdict] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {verdict}
    </span>
  );
}

/**
 * ReportAnalysis renders the detection-pipeline output for one reported
 * message: auto verdict, reasons, IOCs (URLs, domains, attachment hashes,
 * lookalike domains), and auth results.
 */
export function ReportAnalysis({ messageId }: { messageId: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reportedAnalysis', messageId],
    queryFn: async () => (await reportedMessagesApi.analysis(messageId)).data,
    retry: false,
  });

  if (isLoading) {
    return <p className="text-sm text-gray-400">Analyzing...</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-gray-500">No analysis available for this report.</p>;
  }

  const extracted = data.extracted ?? {};
  const urls = extracted.urls ?? [];
  const domains = extracted.url_domains ?? [];
  const attachments = extracted.attachments ?? [];
  const lookalikes = extracted.lookalikes ?? [];
  const auth = extracted.auth ?? {};

  return (
    <section className="space-y-3" aria-label="Detection analysis">
      <h3 className="font-semibold">Detection analysis</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm break-words">
        <dt className="text-gray-500">Verdict</dt>
        <dd><VerdictBadge verdict={data.verdict} /></dd>
        <dt className="text-gray-500">Cluster</dt>
        <dd className="text-gray-900 font-mono">{data.cluster_key || '-'}</dd>
        <dt className="text-gray-500">Auth results</dt>
        <dd className="text-gray-900">
          {auth.dkim || auth.spf || auth.dmarc
            ? `dkim=${auth.dkim || 'n/a'} spf=${auth.spf || 'n/a'} dmarc=${auth.dmarc || 'n/a'}`
            : '-'}
        </dd>
        <dt className="text-gray-500">Analyzed at</dt>
        <dd className="text-gray-900">{data.created_at ? new Date(data.created_at).toLocaleString() : '-'}</dd>
      </dl>
      {data.verdict_reasons?.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-gray-700">
          {data.verdict_reasons.map((reason) => <li key={reason}>{reason}</li>)}
        </ul>
      )}
      {lookalikes.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-red-700">Lookalike domains</h4>
          <ul className="list-disc pl-5 text-sm text-gray-700">
            {lookalikes.map((m) => (
              <li key={`${m.domain}-${m.org_domain}`}>{m.domain} resembles {m.org_domain} ({m.reason})</li>
            ))}
          </ul>
        </div>
      )}
      {urls.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700">URLs ({urls.length})</h4>
          <ul className="list-disc pl-5 text-sm text-gray-700 break-all">
            {urls.map((u) => <li key={u}>{u}</li>)}
          </ul>
        </div>
      )}
      {domains.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700">Link domains</h4>
          <p className="text-sm text-gray-700">{domains.join(', ')}</p>
        </div>
      )}
      {attachments.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700">Attachments</h4>
          <ul className="list-disc pl-5 text-sm text-gray-700">
            {attachments.map((a) => (
              <li key={a.sha256}>{a.filename || '(unnamed)'} <span className="font-mono text-xs">{a.sha256.slice(0, 16)}…</span></li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
